import React from 'react';

export interface BadgeProps extends React.HTMLAttributes<HTMLDivElement> {
  variant?: 
    | 'default' | 'success' | 'warning' | 'danger' | 'info' | 'purple' | 'blue' | 'orange' | 'pink' 
    | 'secondary' | 'neutral' | 'outline' | 'primary' | 'ghost' | 'custom'
    | 'glow-success' | 'glow-danger' | 'glow-warning' | 'glow-info' | 'glow-purple' | 'glow-blue'
    | 'premium-emerald' | 'premium-rose' | 'premium-amber' | 'premium-blue';
  size?: 'xs' | 'sm' | 'md';
}

export const Badge = React.forwardRef<HTMLDivElement, BadgeProps>(
  ({ className = '', variant = 'default', size = 'sm', ...props }, ref) => {
    const sizes = {
      xs: 'px-2 py-0.5 text-xs',
      sm: 'px-2.5 py-0.5 text-xs',
      md: 'px-3 py-1 text-xs',
    };
    const baseStyles = `inline-flex items-center rounded-md border ${sizes[size]} tracking-wider transition-all duration-300`;

    const variants = {
      default: 'bg-slate-100 text-slate-600 border-slate-200/50',
      success: 'bg-emerald-500/10 text-emerald-600 border-emerald-500/20',
      warning: 'bg-amber-500/10 text-amber-600 border-amber-500/20',
      danger: 'bg-red-500/10 text-red-600 border-red-500/20',
      info: 'bg-blue-500/10 text-blue-600 border-blue-500/20',
      purple: 'bg-purple-500/10 text-purple-600 border-purple-500/20',
      blue: 'bg-blue-500/10 text-blue-600 border-blue-500/20',
      orange: 'bg-orange-500/10 text-orange-600 border-orange-500/20',
      pink: 'bg-pink-500/10 text-pink-600 border-pink-500/20',
      secondary: 'bg-slate-100 text-slate-500 border-slate-200/50',
      neutral: 'bg-slate-100 text-slate-500 border-slate-200/50',
      outline: 'bg-transparent text-slate-500 border-slate-200',
      primary: 'bg-blue-600 text-white border-blue-700',
      ghost: 'bg-transparent text-slate-500 border-transparent',
      custom: '',
      
      // Neon Glow Variants
      'glow-success': 'bg-emerald-500 text-white border-emerald-400 shadow-[0_0_10px_rgba(16,185,129,0.4)]',
      'glow-danger': 'bg-rose-500 text-white border-rose-400 shadow-[0_0_10px_rgba(244,63,94,0.4)]',
      'glow-warning': 'bg-amber-500 text-white border-amber-400 shadow-[0_0_10px_rgba(245,158,11,0.4)]',
      'glow-info': 'bg-blue-500 text-white border-blue-400 shadow-[0_0_10px_rgba(59,130,246,0.4)]',
      'glow-purple': 'bg-purple-500 text-white border-purple-400 shadow-[0_0_10px_rgba(168,85,247,0.4)]',
      'glow-blue': 'bg-blue-600 text-white border-blue-500 shadow-[0_0_10px_rgba(37,99,235,0.4)]',

      // Premium Gradient Variants
      'premium-emerald': 'bg-gradient-to-r from-emerald-500 to-teal-500 text-white border-transparent shadow-lg shadow-emerald-500/20 ring-1 ring-white/20',
      'premium-rose': 'bg-gradient-to-r from-rose-500 to-red-500 text-white border-transparent shadow-lg shadow-rose-500/20 ring-1 ring-white/20',
      'premium-amber': 'bg-gradient-to-r from-amber-500 to-orange-500 text-white border-transparent shadow-lg shadow-amber-500/20 ring-1 ring-white/20',
      'premium-blue': 'bg-gradient-to-r from-blue-500 to-indigo-500 text-white border-transparent shadow-lg shadow-blue-500/20 ring-1 ring-white/20',
    };

    return (
      <div ref={ref} className={`${baseStyles} ${variants[variant]} ${className}`} {...props} />
    );
  }
);
Badge.displayName = 'Badge';
